import { z } from 'zod';
import { logger } from '@/lib/utils/logger';

/**
 * Catalog-related Zod schemas for runtime validation
 * Use these schemas to validate API responses from catalog services
 */

// Blockchain validation schema
export const BlockchainSchema = z.object({
  id: z.number(),
  title: z.string(),
  slug: z.string().optional().nullable(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

// Standard (Token Standard) validation schema
export const StandardSchema = z.object({
  id: z.number(),
  title: z.string(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

// Marketplace validation schema
export const MarketplaceSchema = z.object({
  id: z.number(),
  title: z.string(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

// Art Category validation schema
export const ArtCategorySchema = z.object({
  id: z.number(),
  title: z.string(),
  slug: z.string().optional().nullable(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

// Status validation schema (for datastatus relationship)
export const StatusSchema = z.object({
  id: z.number(),
  title: z.string().optional(),
  slug: z.string().optional().nullable(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

// Collection validation schema
export const CollectionSchema = z.object({
  id: z.number(),
  name: z.string(),
  slug: z.string(),
  description: z.string().optional().nullable(),
  status: z.union([z.string(), z.number(), StatusSchema, z.null()]).optional(),
  featured: z.union([z.boolean(), z.number(), z.string(), z.null()]).optional(),
  hidelisting: z.boolean().optional().nullable(),
  artCategory: z.union([
    z.number(),
    z.string(),
    z.array(z.number()),
    z.array(z.string()),
    z.array(ArtCategorySchema),
    ArtCategorySchema,
    z.null(),
  ]).optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

// Artwork validation schema
export const ArtworkSchema = z.object({
  id: z.number(),
  name: z.string(),
  featured: z.union([z.boolean(), z.number(), z.string(), z.null()]).optional(),
  description: z.string().optional().nullable(),
  oneofone: z.boolean().optional().nullable(),
  hidelisting: z.boolean().optional().nullable(),
  tokenId: z.string().optional().nullable(),
  currentPrice: z.number().optional().nullable(),
  lastSalePrice: z.number().optional().nullable(),
  totalSales: z.number().optional().default(0),
  marketLink: z.string().optional().nullable(),
  artworkCreatedDate: z.string().optional().nullable(),
  // Relations - accept number (ID), object (populated), string (slug/title), or null
  blockchain: z.union([
    z.number(),
    z.string(),
    BlockchainSchema,
    z.null(),
  ]).optional(),
  standard: z.union([
    z.number(),
    z.string(),
    StandardSchema,
    z.null(),
  ]).optional(),
  marketplace: z.union([
    z.number(),
    z.string(),
    MarketplaceSchema,
    z.null(),
  ]).optional(),
  collection: z.union([
    z.number(),
    z.string(),
    CollectionSchema,
    z.null(),
  ]).optional(),
  artCategory: z.union([
    z.array(z.number()),
    z.array(z.string()),
    z.array(ArtCategorySchema),
    z.null(),
  ]).optional(),
  // Media fields
  thumbnail: z.string().optional().nullable(),
  image: z.string().optional().nullable(),
  media: z.unknown().optional().nullable(),
  // Timestamps
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

// NFT Collection validation schema
export const NftCollectionSchema = z.object({
  id: z.number(),
  name: z.string(),
  slug: z.string(),
  description: z.string().optional().nullable(),
  featured: z.union([z.boolean(), z.number(), z.string(), z.null()]).optional(),
  hidelisting: z.boolean().optional().nullable(),
  marketLink: z.string().optional().nullable(),
  // Relations - accept number (ID), object (populated), string (slug/title), or null
  blockchain: z.union([
    z.number(),
    z.string(),
    BlockchainSchema,
    z.null(),
  ]).optional(),
  standard: z.union([
    z.number(),
    z.string(),
    StandardSchema,
    z.null(),
  ]).optional(),
  marketplace: z.union([
    z.number(),
    z.string(),
    MarketplaceSchema,
    z.null(),
  ]).optional(),
  // Media
  thumbnail: z.string().optional().nullable(),
  media: z.unknown().optional().nullable(),
  // Timestamps
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

// Exhibition validation schema
export const ExhibitionSchema = z.object({
  id: z.number(),
  name: z.string(),
  slug: z.string(),
  description: z.string().optional().nullable(),
  location: z.string().optional().nullable(),
  startDate: z.string().optional().nullable(),
  endDate: z.string().optional().nullable(),
  featured: z.union([z.boolean(), z.number(), z.string(), z.null()]).optional(),
  hidelisting: z.boolean().optional().nullable(),
  // Media
  thumbnail: z.string().optional().nullable(),
  media: z.unknown().optional().nullable(),
  // Timestamps
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

// Paginated response validation schema
export const PaginatedResponseSchema = z.object({
  docs: z.array(z.unknown()),
  totalDocs: z.number(),
  limit: z.number(),
  totalPages: z.number(),
  page: z.number(),
  pagingCounter: z.number(),
  hasPrevPage: z.boolean(),
  hasNextPage: z.boolean(),
  prevPage: z.number().nullable(),
  nextPage: z.number().nullable(),
});

// API Response validation schema
export const ApiResponseSchema = z.object({
  data: z.unknown(),
  error: z.string().optional().nullable(),
  status: z.number(),
});

// Type exports
export type Artwork = z.infer<typeof ArtworkSchema>;
export type Collection = z.infer<typeof CollectionSchema>;
export type NftCollection = z.infer<typeof NftCollectionSchema>;
export type Exhibition = z.infer<typeof ExhibitionSchema>;
export type Blockchain = z.infer<typeof BlockchainSchema>;
export type Standard = z.infer<typeof StandardSchema>;
export type Marketplace = z.infer<typeof MarketplaceSchema>;
export type ArtCategory = z.infer<typeof ArtCategorySchema>;
export type PaginatedResponse<T> = Omit<z.infer<typeof PaginatedResponseSchema>, 'docs'> & { docs: T[] };
export type ApiResponse<T> = Omit<z.infer<typeof ApiResponseSchema>, 'data'> & { data: T };

/**
 * Validation helper functions
 */

// Safe parse with detailed error logging
export function safeParseWithLogging<T>(
  schema: z.ZodSchema<T>,
  data: unknown,
  context: string
): { success: true; data: T } | { success: false; error: z.ZodError } {
  const result = schema.safeParse(data);
  if (!result.success) {
    logger.error(`[${context}] Validation failed:`, result.error.errors);
  }
  return result;
}

// Parse or throw with context
export function parseWithContext<T>(
  schema: z.ZodSchema<T>,
  data: unknown,
  context: string
): T {
  try {
    return schema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      logger.error(`[${context}] Validation failed:`, error.errors);
      throw new Error(`Validation failed in ${context}: ${error.errors.map(e => e.message).join(', ')}`);
    }
    throw error;
  }
}

// Validate array of items
export function validateArray<T>(
  schema: z.ZodSchema<T>,
  data: unknown[],
  context: string
): T[] {
  return data.map((item, index) =>
    parseWithContext(schema, item, `${context}[${index}]`)
  );
}

// Create paginated response validator
export function createPaginatedValidator<T>(itemSchema: z.ZodSchema<T>) {
  return PaginatedResponseSchema.extend({
    docs: z.array(itemSchema),
  });
}
